import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLang, useT, Trans, LANGS } from "@/i18n/LanguageContext";

function LangSwitcher({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`inline-flex items-center ${compact ? "gap-3" : "gap-3"} text-[0.7rem] tracking-[0.22em] font-medium`}
      data-testid="lang-switcher"
    >
      {LANGS.map((l, idx) => (
        <span key={l.code} className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setLang(l.code)}
            data-testid={`lang-${l.code}`}
            className={`transition-colors ${
              lang === l.code
                ? "text-melilux-gold"
                : "text-melilux-cream/55 hover:text-melilux-cream"
            }`}
          >
            {l.label}
          </button>
          {idx < LANGS.length - 1 && <span className="text-melilux-cream/20">/</span>}
        </span>
      ))}
    </div>
  );
}

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const t = useT();

  const NAV = [
    { to: "/", label: t("nav.home"), id: "home", end: true },
    { to: "/promo", label: t("nav.promo"), id: "promo", highlight: true },
    { to: "/collections", label: t("nav.collections"), id: "collections" },
    { to: "/about", label: t("nav.manifesto"), id: "manifesto" },
    { to: "/contact", label: t("nav.showroom"), id: "showroom" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu on navigation (scroll reset is handled by <ScrollToTop /> in App.js)
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Freeze the page behind the mobile sheet and let Escape dismiss it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Phones get a fully opaque bar (a translucent one lets the page bleed through
  // and reads as a mess against the solid menu sheet); desktop keeps the blur.
  const opaque = scrolled || open;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top)] border-b transition-colors duration-500 ${
        opaque
          ? "bg-melilux-bg md:bg-melilux-bg/85 md:backdrop-blur-xl border-white/10"
          : "bg-transparent border-transparent"
      }`}
      data-testid="site-header"
    >
      {/* Readability scrim while the bar floats over the hero on phones */}
      {!opaque && (
        <div
          className="md:hidden pointer-events-none absolute inset-x-0 top-0 h-[150%] bg-gradient-to-b from-black/60 via-black/25 to-transparent"
          aria-hidden="true"
        />
      )}

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" className="shrink-0" data-testid="brand-link" aria-label="Melilux">
          <img
            src="/melilux_full_logo.png"
            alt="Melilux"
            width={516}
            height={92}
            className="h-7 md:h-8 w-auto"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                n.highlight
                  ? `text-[0.72rem] uppercase tracking-[0.28em] font-medium px-3.5 py-1.5 border transition-colors ${
                      isActive
                        ? "bg-melilux-gold border-melilux-gold text-melilux-bg"
                        : "border-melilux-gold/70 text-melilux-gold hover:bg-melilux-gold hover:text-melilux-bg"
                    }`
                  : `text-[0.72rem] uppercase tracking-[0.28em] font-medium link-underline transition-colors ${
                      isActive
                        ? "text-melilux-gold"
                        : "text-melilux-cream/80 hover:text-melilux-cream"
                    }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-6">
          <div className="hidden md:flex items-center gap-5">
            <LangSwitcher />
            <span className="w-px h-3 bg-white/15" />
            <Link
              to="/contact"
              className="text-[0.72rem] uppercase tracking-[0.28em] font-medium text-melilux-gold link-underline"
              data-testid="header-inquire"
            >
              {t("nav.inquire")}
            </Link>
          </div>
          <button
            className="md:hidden text-melilux-cream p-2.5 -mr-2.5"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile sheet — fills the rest of the viewport in the same solid surface as
          the bar above it, so the two read as one panel. */}
      {open && (
        <div
          className="md:hidden menu-sheet absolute inset-x-0 top-full h-[calc(100svh_-_5rem_-_env(safe-area-inset-top))] bg-melilux-bg flex flex-col"
          data-testid="mobile-menu"
        >
          <nav className="flex-1 overflow-y-auto overscroll-contain px-6">
            {NAV.map((n, i) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.end}
                data-testid={`nav-mobile-${n.id}`}
                className={({ isActive }) =>
                  `flex items-center gap-5 py-5 border-b border-white/[0.07] text-sm uppercase tracking-[0.28em] transition-colors ${
                    isActive || n.highlight ? "text-melilux-gold" : "text-melilux-cream/85"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Fixed-width index keeps every label on the same optical line —
                        including the promo one, which used to sit in a border box. */}
                    <span
                      className={`font-serif text-base w-5 shrink-0 ${
                        isActive ? "text-melilux-gold" : "text-melilux-cream/25"
                      }`}
                      aria-hidden="true"
                    >
                      0{i + 1}
                    </span>
                    <span className="flex-1">{n.label}</span>
                    {n.highlight && <span className="h-1.5 w-1.5 bg-melilux-gold shrink-0" aria-hidden="true" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="border-t border-white/10 px-6 pt-6 pb-[calc(1.5rem_+_env(safe-area-inset-bottom))] flex items-center justify-between gap-4">
            <LangSwitcher compact />
            <Link
              to="/contact"
              data-testid="mobile-inquire"
              className="bg-melilux-gold text-melilux-bg px-6 py-3.5 text-[0.7rem] uppercase tracking-[0.28em] font-medium"
            >
              {t("nav.inquire")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
    const t = useT();
    return (
        <footer className="border-t border-white/10 mt-24 md:mt-32" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
                <div className="md:col-span-5">
                    <p className="overline">{t("footer.brand.overline")}</p>
                    <h3 className="font-serif text-3xl mt-4 leading-tight">
                        <Trans tKey="footer.brand.title" />
                    </h3>
                    <p className="text-sm text-melilux-cream/55 mt-6 max-w-md leading-relaxed">
                        {t("footer.brand.lead")}
                    </p>
                </div>

                <div className="md:col-span-4">
                    <p className="overline">{t("footer.visit")}</p>
                    <p className="text-sm text-melilux-cream/80 mt-4 leading-relaxed">
                        Pod Telecentru<br />
                        Strada Mioriţa 2/1<br />
                        Chișinău, Moldova
                    </p>
                    <p className="text-sm text-melilux-cream/55 mt-3 leading-relaxed">
                        {t("footer.hours.weekdays")}<br />
                        {t("footer.hours.saturday")}<br />
                        {t("footer.closed")}
                    </p>
                    <a href="tel:+37369090408" className="block text-sm text-melilux-gold link-underline mt-3">
                        +373 690 90 408
                    </a>
                </div>

                <div className="md:col-span-3">
                    <p className="overline">{t("footer.discover")}</p>
                    <ul className="mt-4 space-y-3 text-sm text-melilux-cream/80">
                        <li><Link to="/collections" className="link-underline">{t("nav.collections")}</Link></li>
                        <li><Link to="/about" className="link-underline">{t("nav.manifesto")}</Link></li>
                        <li><Link to="/contact" className="link-underline">{t("nav.showroom")}</Link></li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/5 pb-[env(safe-area-inset-bottom)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[0.7rem] uppercase tracking-[0.28em] text-melilux-cream/40">
                    <span>{t("footer.rights", { year: new Date().getFullYear() })}</span>
                    <span>{t("footer.tagline")}</span>
                </div>
            </div>
        </footer>
    );
}

export default function PublicLayout() {
    return (
        <div className="bg-melilux-bg min-h-screen text-melilux-cream font-sans">
            <Header />
            <main className="pt-20">
                <Outlet />
            </main>
            <Footer />
        </div>
    );
}
